import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useQueueDisplay } from '@/features/queue/api';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './queue.module.css';

function chime() {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.2, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.8);
  } catch {
    // audio unavailable (e.g. tests)
  }
}

/** Admin v2 "Waiting-room screen": numbers only, never names. */
export function WaitingRoomPage() {
  const { selected: branch } = useBranch();
  const { hasPermission } = useSession();
  const { data } = useQueueDisplay();
  const screenRef = useRef<HTMLDivElement>(null);
  const lastCalled = useRef<string | null>(null);

  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const key = data?.nowCalling
      ? `${data.nowCalling.number}@${data.nowCalling.calledAt}`
      : null;
    let t: ReturnType<typeof setTimeout> | undefined;
    if (key && lastCalled.current && key !== lastCalled.current) {
      chime();
      // "The number flashes when it changes."
      setFlash(true);
      t = setTimeout(() => setFlash(false), 3000);
    }
    lastCalled.current = key;
    return () => clearTimeout(t);
  }, [data?.nowCalling]);

  const clock = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <PageContainer>
      {!branch && (
        <p className={styles.warnNote}>
          Choose a branch to show its waiting-room screen.
        </p>
      )}
      <div
        ref={screenRef}
        className={styles.screen}
        data-testid="waiting-room-screen"
      >
        <header className={styles.screenHead}>
          <Logo className={styles.screenLogo} monochrome />
          <span>
            {branch?.name} · {clock}
          </span>
        </header>
        <section className={styles.nowCalling}>
          <p className={styles.screenLabel}>Now calling</p>
          <p
            className={[styles.bigNumber, flash && styles.flash]
              .filter(Boolean)
              .join(' ')}
          >
            {data?.nowCalling?.number ?? '—'}
          </p>
          <p className={styles.bigRoom}>{data?.nowCalling?.room ?? ''}</p>
        </section>
        <div className={styles.screenRow}>
          <section className={styles.screenBox}>
            <p className={styles.screenLabel}>Next</p>
            <p className={styles.screenNumbers}>
              {data?.next.length ? data.next.join('  ') : '—'}
            </p>
          </section>
          <section className={styles.screenBox}>
            <p className={styles.screenLabel}>Called</p>
            {/* UAT RL-706: each called number is its own spaced item. */}
            <ul className={styles.calledList} aria-label="Called">
              {data?.called.length ? (
                data.called.slice(0, 4).map((c) => (
                  <li
                    key={`${c.number}-${c.room}`}
                    className={styles.calledItem}
                  >
                    <span>{c.number}</span> <small>{c.room}</small>
                  </li>
                ))
              ) : (
                <li className={styles.calledItem}>—</li>
              )}
            </ul>
          </section>
        </div>
        <p className={styles.screenNote}>
          Numbers only: names are never shown on this screen. A chime sounds and
          the number flashes when it changes.
        </p>
      </div>
      <div className={styles.screenActions}>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void screenRef.current?.requestFullscreen?.()}
        >
          Open full screen
        </Button>
        <Button size="sm" onClick={chime}>
          Test the chime
        </Button>
        {hasPermission('queue:read') && (
          <Link to="/queue" className={styles.linkButton}>
            Back to the queue
          </Link>
        )}
      </div>
    </PageContainer>
  );
}
